/**
 * The vocabulary of the new registration form, mirrored from the backend.
 *
 * `GENDERS`, `PERSON_DOCUMENT_KINDS` and the adult age are all defined server-side
 * (`backend/src/graphql/utils/gender.ts` and `.../personDocuments.ts`) and validated there —
 * these are the display labels for the same codes. **Codes are stored, labels are for display**:
 * renaming a code orphans every row already written with it.
 */

export const GENDER_OPTIONS = [
    { value: 'MALE', label: 'Male' },
    { value: 'FEMALE', label: 'Female' },
    { value: 'TRANSGENDER', label: 'Transgender' },
    { value: 'NOT_STATED', label: 'Prefer not to say' },
];

export const PERSON_DOCUMENT_KIND_OPTIONS = [
    { key: 'THUMBPRINT_SIGNATURE', value: 'THUMBPRINT_SIGNATURE', text: 'Thumbprint signature' },
    { key: 'PERMISSION_SLIP', value: 'PERMISSION_SLIP', text: 'Parental permission slip' },
    { key: 'REGISTRATION_FORM', value: 'REGISTRATION_FORM', text: 'Registration form' },
    { key: 'OTHER', value: 'OTHER', text: 'Other' },
];

/** Mirrors `PERSON_DOCUMENT_CONTENT_TYPES` — the server re-checks, so keep the two in step. */
export const PERSON_DOCUMENT_ACCEPT = 'image/jpeg,image/png,application/pdf';

/** The age at which someone can give consent for themselves. */
export const ADULT_AGE = 18;

/**
 * Whole years old, from a bare `yyyy-MM-dd` string or a `Date` picked in the form.
 *
 * A date of birth is a calendar date, never an instant: a `Date` is read through its *local*
 * accessors and a string through its first ten characters, so neither path can shift the day
 * into the previous one in a browser west of UTC. The day either side of a birthday is what
 * decides whether a guardian may consent for themselves, so this is not cosmetic.
 */
export const ageInYears = (dob: Date | string | null | undefined, asOf: Date = new Date()): number | null => {
    if (dob == null || dob === '') return null;

    let year: number;
    let month: number;
    let day: number;

    if (dob instanceof Date) {
        if (Number.isNaN(dob.getTime())) return null;
        year = dob.getFullYear();
        month = dob.getMonth() + 1;
        day = dob.getDate();
    } else {
        const match = String(dob).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (match == null) return null;
        [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    }

    let age = asOf.getFullYear() - year;

    if (asOf.getMonth() + 1 < month || (asOf.getMonth() + 1 === month && asOf.getDate() < day)) {
        age -= 1;
    }

    return age;
};

/**
 * Under 18 — and `false` when the date of birth is unknown.
 *
 * Deliberately not "unknown means treat as a minor": the guardian section stays greyed until a
 * date of birth says otherwise, rather than demanding parental details from everyone whose age
 * has not been recorded yet.
 */
export const isMinor = (dob: Date | string | null | undefined, asOf: Date = new Date()): boolean => {
    const age = ageInYears(dob, asOf);
    return age != null && age < ADULT_AGE;
};
